import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { api, type Capabilities } from '../api';
import { BrandMark, ErrorNote, Loading, Note } from '../components/ui';
import TalkCard from '../components/TalkCard';
import CallCard, { type CallEndpoints } from '../components/CallCard';

interface LinkInfo {
  appName: string;
  title: string;
  agent: { id: string; name: string; description: string } | null;
  state: 'active' | 'used' | 'expired' | 'revoked';
  budgetSeconds: number;
  remainingSeconds: number;
  web: boolean;
  phone: boolean;
  phoneReason: string | null;
  allowedPrefixes: string[];
  live: { id: string; channel: string } | null;
}

/** "5 min left", "4 min 12 sec left", "40 sec left" — words a prospect reads at a glance. */
function timeLeft(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} sec`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest ? `${m} min ${rest} sec` : `${m} min`;
}

const CLOSED: Record<Exclude<LinkInfo['state'], 'active'>, string> = {
  used: 'The demo time on this link is used up. Thank you for trying it — the team will be in touch.',
  expired: 'This demo link has expired. Ask the person who sent it for a new one.',
  revoked: 'This demo link has been switched off.',
};

/**
 * What a prospect sees on a demo link: no sign-in, one agent, a few minutes in
 * total — in the browser, on their phone, or both. The server holds the clock;
 * this page only shows it.
 */
export default function LinkPage({ token }: { token: string }) {
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [, tick] = useState(0);

  const load = () =>
    api
      .get<LinkInfo>(`/api/link/${encodeURIComponent(token)}`)
      .then((i) => {
        setInfo(i);
        setFetchedAt(Date.now());
        document.title = i.agent ? `Talk to ${i.agent.name.split('·')[0].trim()}` : i.appName;
      })
      .catch(setError);

  useEffect(() => {
    void load();
    // The clock runs on the server; refresh it now and then while the page is open.
    const id = window.setInterval(load, 15_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // While somebody is talking the clock runs down; count it here between refreshes.
  const talking = Boolean(info?.live);
  useEffect(() => {
    if (!talking) return;
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [talking]);

  if (error && !info) {
    return (
      <div className="link-page">
        <ErrorNote error={error} />
      </div>
    );
  }
  if (!info) return <Loading />;

  const name = info.agent?.name.split('·')[0].trim() ?? 'the agent';
  const caps: Capabilities = { phone: info.phone, phoneReason: info.phoneReason, allowedPrefixes: info.allowedPrefixes, voiceCompare: false, limits: null };
  const endpoints: CallEndpoints = {
    place: `/api/link/${token}/call`,
    events: (id) => `/api/link/${token}/conversations/${id}/events`,
    hangup: (id) => `/api/link/${token}/conversations/${id}/hangup`,
    transcript: false,
  };
  const remaining = talking ? info.remainingSeconds - (Date.now() - fetchedAt) / 1000 : info.remainingSeconds;
  const left = timeLeft(remaining);
  const total = timeLeft(info.budgetSeconds);
  // A prospect never sees "phone calling is not set up": no carrier, no phone card.
  const showPhone = info.phone;

  return (
    <div className={`link-page${info.state === 'active' && info.web && showPhone ? '' : ' narrow'}`}>
      <header className="link-head">
        <div className="brand">
          <BrandMark size={30} />
          {info.appName}
        </div>
        <div className={`time-left${info.state === 'active' ? '' : ' over'}`} role="status" aria-live="polite">
          <Clock aria-hidden="true" />
          {info.state === 'active' ? `${left} left` : 'Demo over'}
        </div>
      </header>

      <div className="hero">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>{info.title ? `Prepared for ${info.title}` : 'Live AI voice agent'}</div>
          <h1>Talk to {name}.</h1>
          <p className="sub">
            {info.agent?.description ? `${info.agent.description} ` : ''}Speak naturally in English, Hindi or Hinglish, and interrupt
            whenever you like. You have {total} in total.
          </p>
        </div>
      </div>

      {info.state !== 'active' ? (
        <Note tone="info">{CLOSED[info.state]}</Note>
      ) : (
        <div className={`link-grid${info.web && showPhone ? '' : ' single'}`}>
          {info.web ? (
            <TalkCard
              agentId={info.agent?.id ?? ''}
              agentName={name}
              maxSeconds={info.remainingSeconds}
              socketPath={`/ws/link?t=${encodeURIComponent(token)}`}
              transcriptLink={false}
              diagnostics={false}
              onEnded={load}
            />
          ) : null}
          {showPhone ? (
            <CallCard
              agentId={info.agent?.id ?? ''}
              caps={caps}
              onUsed={load}
              endpoints={endpoints}
              footnote={<span className="small muted">A call uses the same {total}. {left} left.</span>}
            />
          ) : null}
        </div>
      )}
      <p className="small muted" style={{ marginTop: 18 }}>
        A demo of an AI voice agent. Conversations are recorded as text so the team can follow up.
      </p>
    </div>
  );
}
